import React from "react";
import type { MouseEvent, ReactNode } from "react";
import Typography from "@mui/material/Typography";
import type { TypographyProps } from "@mui/material/Typography";
import Box from "@mui/material/Box";
import type { BoxProps } from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import { useDynamicTranslation } from "@/hooks/useDynamicTranslation";

export interface LabelProps {
  id: string | number;
  label?: ReactNode;
  additionalLabel?: string;
  required?: boolean;
  readOnly?: boolean;
  error?: boolean;
  sx?: BoxProps["sx"];
  tooltip?: {
    open: boolean;
    message: ReactNode;
  };
  description?: ReactNode;
}

export function Label({
  id,
  label,
  additionalLabel,
  required,
  readOnly,
  error,
  sx,
  tooltip,
  description,
}: LabelProps) {
  const { t } = useDynamicTranslation("shared");
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [descriptionAnchorEl, setDescriptionAnchorEl] = React.useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const popoverProps = tooltip
    ? {
        onMouseEnter: handlePopoverOpen,
        onMouseLeave: handlePopoverClose,
      }
    : {};

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <Typography
        {...({ htmlFor: id } as TypographyProps)}
        sx={{
          color: error ? "error.main" : "rgba(0, 0, 0, 0.6)",
          ...sx,
        }}
        aria-owns={open ? `mouse-over-popover-${id}` : undefined}
        aria-haspopup="true"
        {...popoverProps}
      >
        {label}
        {required && <span style={{ marginLeft: "3px" }}>*</span>}
        {readOnly && (
          <Typography variant="caption" ml={1}>
            ({t("disabledLabel")})
          </Typography>
        )}
        {additionalLabel && (
          <Typography variant="caption" ml={1}>
            ({additionalLabel})
          </Typography>
        )}
      </Typography>
      {tooltip && (
        <Popover
          id={`mouse-over-popover-${id}`}
          sx={{
            pointerEvents: "none",
            mt: 1,
          }}
          open={tooltip.open ? open : false}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          onClose={handlePopoverClose}
          disableRestoreFocus
        >
          <Box
            {...({
              variant: "body2",
              sx: {
                backgroundColor: "rgba(97, 97, 97)",
                borderRadius: "4px",
                color: "#fff",
                padding: "4px 8px",
                fontSize: "0.6875rem",
                maxWidth: "350px",
                wordWrap: "break-word",
                fontWeight: "500",
              },
            } as BoxProps)}
          >
            {tooltip.message}
          </Box>
        </Popover>
      )}
      {description && (
        <Box sx={{ height: "19.2px", ...sx }}>
          <IconButton
            onClick={(event) => {
              setDescriptionAnchorEl(event.currentTarget);
            }}
            sx={{ p: 0, mx: "4px", mt: "-6px" }}
          >
            <HelpCenterIcon color="primary" fontSize="small" />
          </IconButton>
          <Popover
            id={id as string}
            open={Boolean(descriptionAnchorEl)}
            anchorEl={descriptionAnchorEl}
            onClose={() => setDescriptionAnchorEl(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <Box
              {...({
                variant: "body2",
                sx: {
                  borderRadius: "4px",
                  padding: "4px 8px",
                  fontSize: "0.8rem",
                  maxWidth: "350px",
                  wordWrap: "break-word",
                  fontWeight: "500",
                },
              } as BoxProps)}
            >
              {description}
            </Box>
          </Popover>
        </Box>
      )}
    </Box>
  );
}
